import { useEffect, useRef } from 'react';
import { useVisibility } from './useVisibility';
import { usePaginaNascosta } from './usePaginaNascosta';
import { arenaAwayWs, arenaBackWs, dimenticaPresenzaArena } from '../services/socket';
import { log } from '../utils/logger';

/**
 * Dichiara al server se stiamo guardando l'arena, oppure no.
 *
 * ⚠️ Questo hook NON decide niente: dichiara e basta. E' il rovescio di come
 * funzionava prima, ed e' tutta la correzione.
 *
 * Prima l'unica difesa era `useArenaQueueGuard`, che al passaggio in secondo
 * piano armava un `setTimeout` di cinque secondi e allo scadere chiamava la
 * pausa. Due difetti indipendenti, uno per piattaforma:
 *
 *  - **iOS** sospende il processo della pagina ben prima dei cinque secondi. Al
 *    risveglio o arriva prima l'evento di visibilita' e il timer viene
 *    cancellato, oppure scatta il timer e la rilettura di `visibilityState`
 *    trova gia' `'visible'`: **entrambi gli ordini danno zero pause**.
 *  - **Android e desktop** eseguono il timer, ma la guardia reagisce solo
 *    all'EVENTO «sono appena passato in secondo piano». Chi manda via l'app
 *    mentre sta giocando non arma niente (`arenaStatus` non e' `'searching'`);
 *    poi la partita finisce, il conto alla rovescia dell'overlay — due secondi
 *    — rimette in coda, e nessun altro evento di visibilita' arrivera' mai
 *    perche' si e' gia' nascosti. Si resta accoppiabili a tempo indefinito.
 *
 * Qui l'emit e' **sincrono dentro l'ascoltatore**, cioe' nell'ultimo istante in
 * cui il JS gira di sicuro, e l'attesa la tiene il server sul suo giro di
 * accoppiamento — un orologio che nessun sistema operativo puo' sospendere.
 *
 * ⚠️ La condizione e' **essere iscritti**, non «essere in coda». E' cio' che
 * copre il caso peggiore, l'app messa via *durante* la partita, e non ha
 * controindicazioni: e' il server a decidere cosa farne, e in particolare a non
 * mettere in pausa nessuno a meta' partita.
 *
 * ⚠️ Non si ferma nessun orologio. Chi e' in partita continua a consumare il suo
 * tempo: l'avversario sta aspettando davanti allo schermo, e regalare tempo a
 * chi cambia app sarebbe un exploit banale. E' la stessa scelta dichiarata in
 * `useVisibility` e in `room.ts` lato server.
 *
 * ⚠️ Al ritorno NON si rientra in coda da soli: c'e' il bottone «Riprendi».
 * Rimettersi in coda da soli riaprirebbe la stessa finestra — accoppiati mentre
 * l'utente sta ancora rimettendo a fuoco lo schermo.
 */
export function useArenaPresence(arenaIscrittaId: string | null, arenaStatus: string): void {
  const nascosta = usePaginaNascosta();

  /*
   * `useVisibility` chiama `onHidden` anche su `pagehide`, e di proposito non
   * filtra `persisted`: quindi anche una ricarica o la chiusura della scheda
   * sparano un `arena:away` sull'ultimo socket ancora vivo. E' innocuo, ma solo
   * perche' il server tratta `arena:join` come prova di presenza: la pagina
   * nuova, montandosi a scheda gia' visibile, non emette ne' `visibilitychange`
   * ne' `focus`, quindi senza quella regola l'assenza resterebbe armata e
   * verrebbe messo in pausa chi ha solo ricaricato.
   */
  useVisibility({
    onHidden: () => {
      if (!arenaIscrittaId) return;
      arenaAwayWs(arenaIscrittaId);
    },
    onVisible: () => {
      if (!arenaIscrittaId) return;
      arenaBackWs(arenaIscrittaId);
    },
  });

  /*
   * La regola sullo STATO, che l'evento da solo non puo' dare.
   *
   * Quando si e' gia' nascosti e lo stato dell'arena cambia — tipicamente
   * `gameEnd → searching`, cioe' il conto alla rovescia che rimette in coda —
   * non arrivera' nessun `visibilitychange`, perche' nascosti lo si era gia'.
   * Senza questa ridichiarazione si rientra in coda invisibili al meccanismo, e
   * si viene riaccoppiati partita dopo partita: e' esattamente la segnalazione
   * da cui nasce questo lavoro.
   *
   * Copre anche un `away` non partito perche' il socket era giu' un istante.
   */
  const ultimaDichiarazione = useRef<string | null>(null);
  useEffect(() => {
    /* Non piu' iscritti: il servizio del socket deve smettere di ridichiarare
       una presenza per un'arena che abbiamo lasciato. */
    if (!arenaIscrittaId) dimenticaPresenzaArena();
    if (!arenaIscrittaId || !nascosta) {
      ultimaDichiarazione.current = null;
      return;
    }
    /* Una ridichiarazione per stato, non una per render: l'emit e' idempotente
       lato server, ma il log non deve diventare illeggibile. */
    const chiave = `${arenaIscrittaId}:${arenaStatus}`;
    if (ultimaDichiarazione.current === chiave) return;
    ultimaDichiarazione.current = chiave;
    log.info('Arena', 'presenza: ridichiaro assenza a stato cambiato', { arenaStatus });
    arenaAwayWs(arenaIscrittaId);
  }, [arenaIscrittaId, arenaStatus, nascosta]);
}
